// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Journal Post Page
 *
 * Create and edit journal posts.
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { RichTextEditor } from '@/components/common/editor/RichTextEditor'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Loader2,
  Save,
  FileText,
  Settings,
  Image as ImageIcon,
  ExternalLink,
  Globe,
  EyeOff,
} from 'lucide-react'
import {
  useJournalPost,
  useJournalCategories,
  useCreateJournalPost,
  useUpdateJournalPost,
  useSiteConfig,
} from '../hooks'
import { EaselImageField } from '../components/EaselImageField'

// =============================================================================
// Form Schema
// =============================================================================

const postSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .max(255)
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  excerpt: z.string().max(500).optional(),
  content: z.string().optional(),
  categoryId: z.coerce.number().int().positive().optional().nullable(),
  imageUrl: z.string().url().optional().or(z.literal('')),
  imageAlt: z.string().max(255).optional(),
  published: z.boolean().default(false),
  featured: z.boolean().default(false),
  isQuote: z.boolean().default(false),
  quoteAttribution: z.string().max(255).optional(),
  metaTitle: z.string().max(70).optional(),
  metaDescription: z.string().max(160).optional(),
})

type PostFormData = z.infer<typeof postSchema>

// =============================================================================
// Main Page Component
// =============================================================================

export function JournalPostPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const postId = isNew ? undefined : Number(id)

  const { data: post, isLoading: isLoadingPost } = useJournalPost(postId)
  const { data: categories } = useJournalCategories()
  const { data: siteConfig } = useSiteConfig()
  const createPost = useCreateJournalPost()
  const updatePost = useUpdateJournalPost()

  const form = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      categoryId: null,
      imageUrl: '',
      imageAlt: '',
      published: false,
      featured: false,
      isQuote: false,
      quoteAttribution: '',
      metaTitle: '',
      metaDescription: '',
    },
  })

  useEffect(() => {
    if (post) {
      form.reset({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        categoryId: post.categoryId || null,
        imageUrl: post.imageUrl || '',
        imageAlt: post.imageAlt || '',
        published: post.published ?? false,
        featured: post.featured ?? false,
        isQuote: post.isQuote ?? false,
        quoteAttribution: post.quoteAttribution || '',
        metaTitle: post.metaTitle || '',
        metaDescription: post.metaDescription || '',
      })
    }
  }, [post, form])

  // Auto-generate slug from title
  const watchTitle = form.watch('title')
  useEffect(() => {
    if (isNew && watchTitle) {
      const slug = watchTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
      form.setValue('slug', slug)
    }
  }, [watchTitle, isNew, form])

  /**
   * Save, optionally changing whether the post is live.
   *
   * `published` used to be a switch buried in the sidebar, which meant going live and saving a
   * typo were the same gesture with the same button. Publishing is the consequential action here —
   * it puts writing on a public website — so it gets its own button, and the everyday Save leaves
   * the live/draft state exactly as it was.
   */
  const save = async (publishedOverride?: boolean) => {
    const valid = await form.trigger()
    if (!valid) return

    const data = {
      ...form.getValues(),
      ...(publishedOverride === undefined ? {} : { published: publishedOverride }),
    }

    try {
      if (isNew) {
        const result = await createPost.mutateAsync(data)
        navigate(`/app/easel/journal/${result.id}`)
      } else if (postId) {
        await updatePost.mutateAsync({ id: postId, data })
        form.reset(data, { keepDirty: false })
      }
    } catch (error) {
      // Error handled by mutation
    }
  }

  const isLoading = !isNew && isLoadingPost
  const isSaving = createPost.isPending || updatePost.isPending

  const isPublished = form.watch('published')
  const slug = form.watch('slug')
  // Only a post that is actually live, on a site whose domain we know, has a URL worth offering.
  // `siteUrl` is null for artists nobody has recorded a domain for, and linking to a guess is
  // worse than not linking at all.
  const liveUrl =
    !isNew && isPublished && siteConfig?.siteUrl && slug
      ? `${siteConfig.siteUrl}/journal/${slug}`
      : null

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel/journal')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">
                {isNew ? 'New Post' : 'Edit Post'}
              </h1>
              {!isNew && (
                <Badge variant={isPublished ? 'default' : 'outline'}>
                  {isPublished ? 'Live' : 'Draft'}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {isNew
                ? 'Saved as a draft. Nothing is public until you publish it.'
                : isPublished
                  ? `Live on your site: ${post?.title}`
                  : `Draft, not visible to anyone: ${post?.title}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {liveUrl && (
            <Button asChild variant="outline">
              <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View live
              </a>
            </Button>
          )}

          <Button variant="outline" onClick={() => save()} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isNew ? 'Save draft' : 'Save'}
          </Button>

          {isPublished && !isNew ? (
            <Button variant="outline" onClick={() => save(false)} disabled={isSaving}>
              <EyeOff className="h-4 w-4 mr-2" />
              Unpublish
            </Button>
          ) : (
            <Button onClick={() => save(true)} disabled={isSaving}>
              <Globe className="h-4 w-4 mr-2" />
              Publish
            </Button>
          )}
        </div>
      </div>

      <Form {...form}>
        {/* Submission goes through the header buttons, which say what they each do. A bare submit
            would have to pick one of publish/save on the artist's behalf. */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Post title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slug</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="post-slug" />
                        </FormControl>
                        <FormDescription>URL-friendly identifier</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="excerpt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Excerpt</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="A brief summary of the post..."
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Short description shown in post lists
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                            placeholder="Write your post…"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Quote Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Quote Post</CardTitle>
                  <CardDescription>
                    Turn this into a quote post for shorter, impactful content
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="isQuote"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="font-normal">This is a quote post</FormLabel>
                      </FormItem>
                    )}
                  />

                  {form.watch('isQuote') && (
                    <FormField
                      control={form.control}
                      name="quoteAttribution"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Attribution</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Quote source or author" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Publish Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Publishing lives in the header, next to Save, because it is the action with
                      consequences outside this screen. Repeating it as a switch here would give
                      the same decision two controls that can disagree. */}
                  <div className="rounded-lg border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Status</p>
                      <Badge variant={isPublished ? 'default' : 'outline'}>
                        {isPublished ? 'Live' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {isPublished
                        ? 'Anyone can read this on your site. Unpublish from the top of the page.'
                        : 'Only you can see this. Publish from the top of the page when it is ready.'}
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="featured"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Featured</FormLabel>
                          <FormDescription className="text-xs">
                            Highlight this post
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="categoryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select
                          value={field.value?.toString() || ''}
                          onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories?.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id.toString()}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Featured Image */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Featured Image
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <EaselImageField
                            kind="journal"
                            aspectHint="16:9"
                            value={field.value}
                            alt={form.watch('imageAlt')}
                            onChange={(url, meta) => {
                              field.onChange(url)
                              if (meta && meta.alt !== undefined) {
                                form.setValue('imageAlt', meta.alt)
                              }
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Upload a file, pick one of your artworks, or paste a URL
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* SEO Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>SEO</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="metaTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="SEO title (optional)" />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Max 70 characters
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="metaDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="SEO description (optional)"
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Max 160 characters
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
